import React, { useState, useEffect, useContext } from "react";
import { TranslatorContext } from "../../../../context/Translator";
import { useNavigate, useParams, useLocation, Link } from "react-router-dom";
import { Row, Col, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../../../layouts/PageLayout";

export default function CheckerAddEdit() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        nickname: "",
        per_product_amount: "",
        password: "",
        password_confirmation: "",
    });

    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [pageLoading, setPageLoading] = useState(isEdit && !location.state?.checker);
    const [alert, setAlert] = useState(null);

    const getAuthHeader = () => ({
        headers: {
            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
    });

    useEffect(() => {
        if (isEdit) {
            if (location.state?.checker) {
                const item = location.state.checker;
                setFormData({
                    name: item.name || "",
                    email: item.email || "",
                    nickname: item.nickname || "",
                    per_product_amount: item.per_product_amount || item.per_product_checking_amount || "",
                    password: "",
                    password_confirmation: "",
                });
            } else {
                const fetchChecker = async () => {
                    try {
                        const res = await axios.get(
                            `${process.env.REACT_APP_API_URL}/admin/checker`,
                            getAuthHeader()
                        );
                        if (res.data?.success) {
                            const item = res.data.data.find((c) => c.id === parseInt(id));
                            if (item) {
                                setFormData({
                                    name: item.name || "",
                                    email: item.email || "",
                                    nickname: item.nickname || "",
                                    per_product_amount: item.per_product_amount || item.per_product_checking_amount || "",
                                    password: "",
                                    password_confirmation: "",
                                });
                            } else {
                                setAlert({ type: "danger", message: "Checker not found." });
                            }
                        }
                    } catch (err) {
                        setAlert({ type: "danger", message: "Failed to load checker details." });
                    } finally {
                        setPageLoading(false);
                    }
                };
                fetchChecker();
            }
        }
    }, [id, isEdit, location.state]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setErrors({});
        setAlert(null);

        const url = isEdit
            ? `${process.env.REACT_APP_API_URL}/admin/checker/${id}`
            : `${process.env.REACT_APP_API_URL}/admin/checker`;

        try {
            const response = await axios.post(url, formData, getAuthHeader());

            if (response.data?.success) {
                navigate("/checkers");
            }
        } catch (err) {
            if (err.response?.status === 422) {
                setErrors(err.response.data.errors || {});
            } else {
                setAlert({
                    type: "danger",
                    message: err.response?.data?.message || "Operation failed. Please try again.",
                });
            }
        } finally {
            setSubmitting(false);
        }
    };

    if (pageLoading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("Loading checker data...")}</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb d-flex justify-content-between align-items-center">
                            <div>
                                <h3 className="mc-breadcrumb-title">
                                    {isEdit ? t("Edit Checker") : t("Add Checker")}
                                </h3>
                                <ul className="mc-breadcrumb-list">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link">
                                            {t("Home")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        <Link to="/checkers" className="mc-breadcrumb-link">
                                            {t("Checkers")}
                                        </Link>
                                    </li>
                                    <li className="mc-breadcrumb-item">
                                        {isEdit ? t("Edit") : t("Add")}
                                    </li>
                                </ul>
                            </div>
                            <Button variant="secondary" onClick={() => navigate("/checkers")}>
                                {t("Back")}
                            </Button>
                        </div>
                    </div>
                </Col>

                <Col xl={8} className="mx-auto">
                    <Card>
                        <Card.Header className="bg-white">
                            <h5 className="mb-0">
                                {isEdit ? `${t("Update Checker Details")}` : `${t("Create New Checker")}`}
                            </h5>
                        </Card.Header>
                        <Card.Body>
                            {alert && (
                                <Alert variant={alert.type} dismissible onClose={() => setAlert(null)}>
                                    {alert.message}
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Name *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        isInvalid={!!errors.name}
                                        placeholder="Enter full name"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.name?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Nickname *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="nickname"
                                        value={formData.nickname}
                                        onChange={handleChange}
                                        isInvalid={!!errors.nickname}
                                        placeholder="Enter nickname"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.nickname?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Email *</Form.Label>
                                    <Form.Control
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        isInvalid={!!errors.email}
                                        placeholder="Enter email address"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.email?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>Per Product Amount ($) *</Form.Label>
                                    <Form.Control
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        name="per_product_amount"
                                        value={formData.per_product_amount}
                                        onChange={handleChange}
                                        isInvalid={!!errors.per_product_amount || !!errors.per_product_checking_amount}
                                        placeholder="Enter rate per checked product (e.g. 5.00)"
                                        required
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.per_product_amount?.[0] || errors.per_product_checking_amount?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                <Form.Group className="mb-3">
                                    <Form.Label>
                                        {isEdit ? "New Password (Optional)" : "Password *"}
                                    </Form.Label>
                                    <Form.Control
                                        type="password"
                                        name="password"
                                        value={formData.password}
                                        onChange={handleChange}
                                        isInvalid={!!errors.password}
                                        placeholder={
                                            isEdit
                                                ? "Leave empty if you don't want to change"
                                                : "Minimum 8 characters"
                                        }
                                        required={!isEdit}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.password?.[0]}
                                    </Form.Control.Feedback>
                                </Form.Group>

                                {(formData.password || !isEdit) && (
                                    <Form.Group className="mb-4">
                                        <Form.Label>Confirm Password *</Form.Label>
                                        <Form.Control
                                            type="password"
                                            name="password_confirmation"
                                            value={formData.password_confirmation}
                                            onChange={handleChange}
                                            placeholder="Confirm password"
                                            required={!isEdit || Boolean(formData.password)}
                                        />
                                    </Form.Group>
                                )}

                                <div className="d-flex gap-2 justify-content-end">
                                    <Button
                                        variant="secondary"
                                        onClick={() => navigate("/checkers")}
                                    >
                                        Cancel
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={submitting}>
                                        {submitting
                                            ? isEdit
                                                ? "Updating..."
                                                : "Saving..."
                                            : isEdit
                                                ? "Update Checker"
                                                : "Save Checker"}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}